/**
 * In-memory mock backend for the Manager Admin page.
 *
 * Shares the single mock toggle with the Knowledge Base (see
 * Knowledge/mockMode.js `isMockMode`). When mock mode is on, the admin
 * dashboard is served entirely from this in-memory store — no ACC Lambda, no
 * backbone, no AWS. State is mutable for the session (resets on full reload) so
 * assigning tasks / required reading / role changes reflect immediately.
 *
 * Mirrors the { success, data } envelope that AdminUtils expects, matching the
 * real backend contract we'll wire up later.
 */

import { isMockMode } from '../Knowledge/mockMode';
import { businessToday } from '../Tasks/TaskUtils';

export { isMockMode };

const NETWORK_DELAY_MS = 140;

const iso = (daysFromNow = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    return d.toISOString();
};

const dateOnly = (daysFromNow = 0) => iso(daysFromNow).slice(0, 10);

let idSeq = 500;
const nextId = (prefix) => `${prefix}-${(idSeq += 1)}`;

export const ROLE_OPTIONS = ['HeroTrainer', 'Manager', 'Admin'];

// --- Seed data -------------------------------------------------------------

const seedTrainers = () => [
    {
        id: 't-alex',
        name: 'Alex Rivera',
        role: 'HeroTrainer',
        gym: 'Arena One',
        active: true,
        joinedAt: iso(-420),
        kb: { requiredAssigned: 5, requiredAcked: 5, overdue: 0, lastAckAt: iso(-2) },
        tasks: { assigned: 20, completed: 18, overdue: 1 },
        daily: { submittedToday: true, streakDays: 12, lastSubmittedAt: iso(0) },
        scheduledToday: true,
        issues: { bugsReported: 2, incidentsReported: 0 },
        recentTasks: [
            { id: 'tk-a1', name: 'Open checklist', status: 'Completed', dueDate: dateOnly(-1), priority: 'Medium' },
            { id: 'tk-a2', name: 'Reset booth 3', status: 'In Progress', dueDate: dateOnly(0), priority: 'High' },
            { id: 'tk-a3', name: 'Inventory count', status: 'Overdue', dueDate: dateOnly(-2), priority: 'Low' },
        ],
        kbAssignments: [
            { articleId: 'a-safety', title: 'Safety & Emergency Procedures', acknowledged: true, dueDate: dateOnly(-5) },
            { articleId: 'a-cancel-save', title: 'Cancellation-Save SOP', acknowledged: true, dueDate: dateOnly(-3) },
        ],
    },
    {
        id: 't-priya',
        name: 'Priya Patel',
        role: 'HeroTrainer',
        gym: 'Arena One',
        active: true,
        joinedAt: iso(-300),
        kb: { requiredAssigned: 5, requiredAcked: 4, overdue: 0, lastAckAt: iso(-1) },
        tasks: { assigned: 25, completed: 22, overdue: 2 },
        daily: { submittedToday: true, streakDays: 5, lastSubmittedAt: iso(0) },
        scheduledToday: true,
        issues: { bugsReported: 0, incidentsReported: 1 },
        recentTasks: [
            { id: 'tk-p1', name: 'Member follow-ups', status: 'Completed', dueDate: dateOnly(-1), priority: 'Medium' },
            { id: 'tk-p2', name: 'Restock waivers', status: 'Overdue', dueDate: dateOnly(-2), priority: 'Medium' },
        ],
        kbAssignments: [
            { articleId: 'a-cancel-save', title: 'Cancellation-Save SOP', acknowledged: true, dueDate: dateOnly(-3) },
            { articleId: 'a-welcome', title: 'Welcome & First Shift Checklist', acknowledged: false, dueDate: dateOnly(2) },
        ],
    },
    {
        id: 't-sam',
        name: 'Sam Okafor',
        role: 'HeroTrainer',
        gym: 'Arena Two',
        active: true,
        joinedAt: iso(-90),
        kb: { requiredAssigned: 5, requiredAcked: 2, overdue: 2, lastAckAt: iso(-15) },
        tasks: { assigned: 15, completed: 9, overdue: 4 },
        daily: { submittedToday: false, streakDays: 0, lastSubmittedAt: iso(-3) },
        scheduledToday: true,
        issues: { bugsReported: 5, incidentsReported: 2 },
        recentTasks: [
            { id: 'tk-s1', name: 'Close checklist', status: 'Overdue', dueDate: dateOnly(-2), priority: 'High' },
            { id: 'tk-s2', name: 'Clean VR lenses', status: 'Overdue', dueDate: dateOnly(-1), priority: 'Medium' },
            { id: 'tk-s3', name: 'Shadow new hire', status: 'New', dueDate: dateOnly(1), priority: 'Low' },
        ],
        kbAssignments: [
            { articleId: 'a-safety', title: 'Safety & Emergency Procedures', acknowledged: false, dueDate: dateOnly(-4) },
            { articleId: 'a-cancel-save', title: 'Cancellation-Save SOP', acknowledged: false, dueDate: dateOnly(-1) },
        ],
    },
    {
        id: 't-maria',
        name: 'Maria Gomez',
        role: 'HeroTrainer',
        gym: 'Arena Two',
        active: true,
        joinedAt: iso(-540),
        kb: { requiredAssigned: 5, requiredAcked: 5, overdue: 0, lastAckAt: iso(-1) },
        tasks: { assigned: 31, completed: 30, overdue: 0 },
        daily: { submittedToday: true, streakDays: 21, lastSubmittedAt: iso(0) },
        scheduledToday: true,
        issues: { bugsReported: 1, incidentsReported: 0 },
        recentTasks: [
            { id: 'tk-m1', name: 'Lead morning brief', status: 'Completed', dueDate: dateOnly(0), priority: 'Medium' },
            { id: 'tk-m2', name: 'Audit lost & found', status: 'Completed', dueDate: dateOnly(-1), priority: 'Low' },
        ],
        kbAssignments: [
            { articleId: 'a-safety', title: 'Safety & Emergency Procedures', acknowledged: true, dueDate: dateOnly(-5) },
        ],
    },
    {
        id: 't-devin',
        name: 'Devin Brooks',
        role: 'HeroTrainer',
        gym: 'Arena One',
        active: true,
        joinedAt: iso(-45),
        kb: { requiredAssigned: 5, requiredAcked: 3, overdue: 1, lastAckAt: iso(-6) },
        tasks: { assigned: 20, completed: 12, overdue: 3 },
        daily: { submittedToday: false, streakDays: 2, lastSubmittedAt: iso(-1) },
        scheduledToday: false,
        issues: { bugsReported: 3, incidentsReported: 1 },
        recentTasks: [
            { id: 'tk-d1', name: 'Greeter rotation', status: 'In Progress', dueDate: dateOnly(0), priority: 'Medium' },
            { id: 'tk-d2', name: 'Update booth signage', status: 'Overdue', dueDate: dateOnly(-3), priority: 'Low' },
        ],
        kbAssignments: [
            { articleId: 'a-welcome', title: 'Welcome & First Shift Checklist', acknowledged: true, dueDate: dateOnly(-2) },
            { articleId: 'a-safety', title: 'Safety & Emergency Procedures', acknowledged: false, dueDate: dateOnly(-1) },
        ],
    },
    {
        id: 't-jordan',
        name: 'Jordan Lee',
        role: 'Manager',
        gym: 'Arena One',
        active: true,
        joinedAt: iso(-900),
        kb: { requiredAssigned: 5, requiredAcked: 5, overdue: 0, lastAckAt: iso(-4) },
        tasks: { assigned: 14, completed: 14, overdue: 0 },
        daily: { submittedToday: true, streakDays: 30, lastSubmittedAt: iso(0) },
        scheduledToday: true,
        issues: { bugsReported: 0, incidentsReported: 0 },
        recentTasks: [],
        kbAssignments: [],
    },
];

// Standalone counters not derived from trainers.
const seedIssueFeed = () => ({
    openBugs: 6,
    openIncidents: 2,
    last7DaysBugs: 11,
    last7DaysIncidents: 3,
    recent: [
        { id: 'iss-1', type: 'bug', title: 'POS freezes on split tender', reporter: 'Sam Okafor', status: 'Open', at: iso(-1) },
        { id: 'iss-2', type: 'incident', title: 'Guest slip near booth 2', reporter: 'Priya Patel', status: 'Reviewing', at: iso(-2) },
        { id: 'iss-3', type: 'bug', title: 'Headset 7 wont pair', reporter: 'Devin Brooks', status: 'Open', at: iso(-2) },
        { id: 'iss-4', type: 'bug', title: 'Waiver email not sending', reporter: 'Sam Okafor', status: 'Open', at: iso(-4) },
    ],
});

const seedFlaggedStale = () => [
    { articleId: 'a-pos', title: 'Using the POS Terminal', flaggedBy: 'Sam Okafor', note: 'Screens changed after update', at: iso(-2) },
    { articleId: 'a-network', title: 'Network Outage Playbook', flaggedBy: 'Devin Brooks', note: 'Still a draft', at: iso(-1) },
];

const seedAnnouncements = () => [
    {
        id: 'ann-1',
        title: 'Holiday hours this weekend',
        body: 'We close at 8pm Sat & Sun. Adjust your closing checklists accordingly and remind guests at booking.',
        level: 'info',
        author: 'Jordan Lee',
        gym: 'All',
        createdAt: iso(-1),
        expiresAt: iso(5),
    },
    {
        id: 'ann-2',
        title: 'Headset 7 is out of service',
        body: 'Booth 3 / headset 7 is bagged for repair — do not assign guests to it until further notice.',
        level: 'warning',
        author: 'Jordan Lee',
        gym: 'Arena One',
        createdAt: iso(0),
        expiresAt: iso(3),
    },
];

export const ANNOUNCEMENT_LEVELS = ['info', 'warning', 'critical'];
export const GYMS = ['Arena One', 'Arena Two'];

const ASSIGNABLE_ARTICLES = [
    { id: 'a-welcome', title: 'Welcome & First Shift Checklist' },
    { id: 'a-safety', title: 'Safety & Emergency Procedures' },
    { id: 'a-pos', title: 'Using the POS Terminal' },
    { id: 'a-cancel-save', title: 'Cancellation-Save SOP' },
    { id: 'a-refund', title: 'Issuing Refunds' },
    { id: 'a-vr-reboot', title: "VR Headset Won't Track — Reboot Steps" },
    { id: 'a-glossary', title: 'Glossary of Terms' },
];

/**
 * Backend rollout switches, with the defaults the Lambda ships. `source` is
 * `stored` once somebody has set one and `default` until then, which is what
 * tells an admin whether a value was chosen or merely inherited.
 */
const FEATURE_FLAG_DEFAULTS = {
    REQUIRE_ACC_SESSION: false,
    LEGACY_CLIENT_CHECKOFFS: true,
    PHOTO_EVIDENCE_ENABLED: false,
};

// --- Mutable store ---------------------------------------------------------

let store = null;

const getStore = () => {
    if (!store) {
        store = {
            trainers: seedTrainers(),
            issues: seedIssueFeed(),
            flaggedStale: seedFlaggedStale(),
            announcements: seedAnnouncements(),
            handoffs: [
                {
                    id: 'ho-1',
                    body: 'Booth 2 tracking drift — reboot mid-shift if members complain.',
                    gymId: null,
                    author: 'Alex Rivera',
                    createdAt: iso(0),
                    active: true,
                },
            ],
            supplySignals: [
                {
                    id: 'sup-1',
                    text: 'Protein cups running low (≈1 day left)',
                    source: 'ht',
                    status: 'open',
                    createdAt: iso(-1),
                },
            ],
            operatingHours: {},
            shiftAudits: {},
            featureFlags: {},
            featureFlagsMeta: { updatedAt: null, updatedBy: null },
        };
    }
    return store;
};

const featureFlagsView = () => {
    const stored = getStore().featureFlags;
    const flags = {};
    Object.entries(FEATURE_FLAG_DEFAULTS).forEach(([name, fallback]) => {
        const set = Object.prototype.hasOwnProperty.call(stored, name);
        flags[name] = {
            value: set ? stored[name] : fallback,
            source: set ? 'db' : 'default',
            default: fallback,
        };
    });
    return { flags, ...getStore().featureFlagsMeta };
};

// --- Shift audit -----------------------------------------------------------

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const AUDIT_RANGE_CAP_DAYS = 60;
// Command verb -> stored review outcome, mirroring the server.
const REVIEW_OUTCOMES = { confirm: 'confirmed', reject: 'rejected', discussed: 'discussed' };
// Both outcomes that record what was said need it said.
const OUTCOMES_NEEDING_NOTE = ['reject', 'discussed'];
const REVIEW_NOTE_MAX = 500;

const shiftDay = (day, offset) => {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + offset)).toISOString().slice(0, 10);
};

// Gym-local (UTC-6) time on the business day, so fixtures land in the close window.
const closeWindowIso = (day, hms) =>
    new Date(`${day}T${hms.length === 5 ? `${hms}:00` : hms}-06:00`).toISOString();

// Evidence keys follow TaskEvidence.py: {gym}/{date}/{trainer}/{taskId}/{uuid}.jpg
const evidenceFor = (businessDate, gymId, taskId, uploadedAt, seed, takenAt) => ({
    photoKey: `${gymId}/${businessDate}/alex-rivera/${taskId}/${seed}.jpg`,
    sha256: seed.repeat(8).slice(0, 64),
    bytes: 184320,
    uploadedAt,
    takenAt,
});

const seedTimeline = (businessDate, gymId) => {
    const at = (hms) => closeWindowIso(businessDate, hms);
    const event = (n, overrides) => ({
        id: `evt-${businessDate}-${gymId}-${n}`,
        completedBy: 'Alex Rivera',
        businessDate,
        completedAt: null,
        taskId: '',
        taskName: '',
        shiftPhase: 'close',
        requirementLevel: 'required',
        outcome: 'done',
        outcomeLabel: 'Done',
        action: 'completed',
        detail: '',
        flowRunId: `${businessDate}#${gymId}#close#alex-rivera`,
        clientTs: null,
        replayed: false,
        recordedBy: null,
        ...overrides,
    });
    return [
        event(1, { taskId: 'seed-close-shutdown-booths', taskName: 'Close: shut down booths', completedAt: at('19:36'), clientTs: at('19:36') }),
        event(2, {
            taskId: 'seed-close-ipad-14', taskName: 'Close: plug in iPad 14', completedAt: at('19:37:05'), clientTs: at('19:37:05'),
            evidence: evidenceFor(businessDate, gymId, 'seed-close-ipad-14', at('19:37:02'), '7c1e9a4b', at('19:36:50')),
            // Haiku could not see the cable; the nightly Opus pass agreed, so the flag stands for a manager.
            verification: {
                status: 'flagged', reason: 'uncertain', issues: ['Cable not visible'], confidence: 0.41,
                model: 'claude-haiku-4-5', photoPolicy: 'required', escalatedModel: 'claude-opus-5',
                firstPass: { status: 'flagged', reason: 'uncertain', confidence: 0.38, model: 'claude-haiku-4-5' },
            },
        }),
        event(3, {
            taskId: 'seed-close-vacuum', taskName: 'Close: vacuum', requirementLevel: 'optional',
            outcome: 'not_needed', outcomeLabel: 'Not needed', detail: 'Floor was clean',
            completedAt: at('19:37:30'), clientTs: at('19:37:30'),
        }),
        event(4, { taskId: 'seed-close-mop', taskName: 'Close: mop floors as needed', completedAt: at('19:38'), clientTs: at('19:38') }),
        event(5, {
            taskId: 'seed-close-furniture', taskName: 'Close: wipe furniture, HT station',
            completedAt: at('19:38:20'), clientTs: at('19:15'), replayed: true,
            evidence: evidenceFor(businessDate, gymId, 'seed-close-furniture', at('19:15:40'), 'd2f08e61', at('17:28:20')),
            verification: { status: 'verified', confidence: 0.93, model: 'claude-haiku-4-5', photoPolicy: 'required' },
        }),
        event(6, {
            taskId: 'seed-close-arm-lock', taskName: 'Close: arm alarm, lock up',
            completedAt: at('20:20'), flowRunId: null, recordedBy: 'Mock Manager',
        }),
    ];
};

const seedFindings = (businessDate, timeline) => {
    const finding = (overrides) => ({ id: nextId('sf'), trainer: 'Alex Rivera', taskId: '', taskName: '', eventId: '', ...overrides });
    const forEvent = (taskId) => {
        const e = timeline.find((row) => row.taskId === taskId);
        return { taskId: e.taskId, taskName: e.taskName, eventId: e.id, at: e.completedAt };
    };
    return [
        finding({ code: 'burst', severity: 'soft', message: '5 tasks checked off within 40 seconds', ...forEvent('seed-close-mop') }),
        finding({
            code: 'report_with_open_close', severity: 'medium',
            message: 'Daily report submitted with 2 close tasks open', at: closeWindowIso(businessDate, '19:42'),
        }),
        finding({
            code: 'headsets_on_after_shutdown', severity: 'strong',
            message: 'Booth 7 headset still on 20 min after shutdown was checked off',
            ...forEvent('seed-close-shutdown-booths'), at: closeWindowIso(businessDate, '19:56'),
        }),
        finding({ code: 'replayed', severity: 'soft', message: 'Replayed from the offline queue; client time 7:15pm', ...forEvent('seed-close-furniture') }),
        finding({ code: 'photo_uncertain', severity: 'review', message: 'Photo check uncertain: cable not visible', ...forEvent('seed-close-ipad-14') }),
        finding({
            code: 'photo_stale', severity: 'medium',
            message: 'Photo was taken 2h 10m before the check-off',
            ...forEvent('seed-close-furniture'),
        }),
        finding({
            code: 'report_not_submitted', severity: 'medium', trainer: 'Priya Patel',
            message: 'No daily report an hour after the shift ended; one was auto-submitted',
            at: closeWindowIso(businessDate, '15:05'),
        }),
        finding({
            code: 'testimonials_short', severity: 'medium',
            taskId: 'seed-member-testimonials', taskName: 'Get 2+ member testimonials',
            message: 'Marked testimonials done with 1 recorded (2 needed)',
            at: closeWindowIso(businessDate, '20:45'),
        }),
    ];
};

// Rolling 30-day photo trust per trainer (TaskEvidence.trust_score); fewer than five photos is no opinion.
const TRUST_FIXTURE = {
    'Alex Rivera': { score: 0.92, verified: 17, flagged: 1, rejected: 0, sample: 18 },
    'Priya Patel': { score: 0.78, verified: 7, flagged: 2, rejected: 0, sample: 9 },
    'Maria Gomez': { score: 0.86, verified: 12, flagged: 1, rejected: 1, sample: 14 },
    'Sam Okafor': { score: 1, verified: 2, flagged: 0, rejected: 0, sample: 0 },
};

const trustFor = (trainerName, days) => ({
    trainer: trainerName,
    windowDays: Number(days) || 30,
    ...(TRUST_FIXTURE[trainerName] || { score: 1, verified: 0, flagged: 0, rejected: 0, sample: 0 }),
});

/** Inline SVG stand-in for the presigned GET so thumbnails render with no bucket. */
const evidencePlaceholder = (photoKey) => {
    const parts = String(photoKey).split('/');
    const label = (parts[3] || parts[parts.length - 1] || 'photo').replace(/[<>&"']/g, '');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="480" viewBox="0 0 640 480">`
        + `<rect width="640" height="480" fill="#2a2a2a"/>`
        + `<rect x="40" y="40" width="560" height="400" rx="16" fill="#3a3a3a" stroke="#4a90e2" stroke-width="4"/>`
        + `<text x="320" y="230" text-anchor="middle" font-family="sans-serif" font-size="28" fill="#ffffff">Mock evidence</text>`
        + `<text x="320" y="272" text-anchor="middle" font-family="monospace" font-size="18" fill="#b8b8b8">${label}</text>`
        + `</svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const seedBoothSnapshot = (businessDate) => ({
    label: 'nightly',
    takenAt: closeWindowIso(businessDate, '20:30'),
    summary: { total: 14, headsetsOff: 13, headsetsOn: 1, unknown: 0, pcOnline: 1 },
    booths: Array.from({ length: 14 }, (_, i) => {
        const boothNumber = i + 1;
        const on = boothNumber === 7;
        return { boothNumber, headsetConnected: on, pcState: on ? 'online' : 'offline' };
    }),
});

/**
 * An audited day carries summary, findings, snapshots and digest. A day the
 * nightly job has not reached yet is the same shell with only the timeline.
 */
const seedShiftAudit = (businessDate, gymId, { audited }) => {
    const timeline = seedTimeline(businessDate, gymId);
    const shell = {
        id: `${businessDate}#${gymId}`,
        businessDate,
        gymId,
        generatedAt: null,
        trigger: null,
        runCount: 0,
        summary: null,
        findings: [],
        boothSnapshots: [],
        digestPostedAt: null,
        digestText: '',
        timeline,
    };
    if (!audited) return shell;
    const nextMorning = closeWindowIso(shiftDay(businessDate, 1), '06:32');
    return {
        ...shell,
        generatedAt: nextMorning,
        trigger: 'nightly',
        runCount: 1,
        summary: {
            completions: 6,
            close: { done: 5, not_needed: 1, blocked: 0, missed: 0 },
            trainers: ['Alex Rivera'],
            lastCloseCompletionAt: closeWindowIso(businessDate, '20:20'),
            // Three reports for the day: one Alex filed himself, one the server
            // filed for Priya an hour after her shift ended, and Sam's auto
            // report with the real one he got to later.
            reports: [
                {
                    id: `${businessDate}_Alex Rivera`, kind: 'close', trainer: 'Alex Rivera',
                    submittedAt: closeWindowIso(businessDate, '19:42'), pendingClose: 2,
                    autoSubmitted: false, autoReason: '', shiftEndAt: '',
                    autoSummary: null, supersededBy: '', supersedes: '',
                },
                {
                    id: `${businessDate}#${gymId}#auto#Priya Patel`, kind: 'auto', trainer: 'Priya Patel',
                    submittedAt: closeWindowIso(businessDate, '15:05'),
                    autoSubmitted: true, autoReason: 'not_submitted_within_hour',
                    shiftEndAt: closeWindowIso(businessDate, '14:00'),
                    pendingClose: 0, supersededBy: '', supersedes: '',
                    autoSummary: {
                        done: ['Wipe down headsets between sessions', 'Open: bathrooms'],
                        notNeeded: [{ task: 'Open: boot booths', reason: 'Already on from the night before' }],
                        blocked: [{ task: 'Restock the fridge', reason: 'Delivery never arrived' }],
                        missed: ['Pre-open safety walk'],
                        testimonials: 1,
                        completions: 3,
                    },
                },
                {
                    id: `${businessDate}#${gymId}#auto#Sam Okafor`, kind: 'auto', trainer: 'Sam Okafor',
                    submittedAt: closeWindowIso(businessDate, '17:05'),
                    autoSubmitted: true, autoReason: 'not_submitted_within_hour',
                    shiftEndAt: closeWindowIso(businessDate, '16:00'),
                    pendingClose: 0, supersededBy: `${businessDate}_Sam Okafor`, supersedes: '',
                    autoSummary: {
                        done: ['Mid: sweep the lobby'],
                        notNeeded: [],
                        blocked: [],
                        missed: [],
                        testimonials: 0,
                        completions: 1,
                    },
                },
                {
                    id: `${businessDate}_Sam Okafor`, kind: 'close', trainer: 'Sam Okafor',
                    submittedAt: closeWindowIso(businessDate, '21:12'), pendingClose: 0,
                    autoSubmitted: false, autoReason: '', shiftEndAt: '',
                    // An object field has no sensible empty string; the server sends null.
                    autoSummary: null, supersededBy: '',
                    supersedes: `${businessDate}#${gymId}#auto#Sam Okafor`,
                },
            ],
            headsets: { off: 13, on: 1, unknown: 0, takenAt: closeWindowIso(businessDate, '20:30') },
            flowFinishedAt: null,
            photos: { verified: 1, flagged: 1, recorded: 0, skipped: 0, pending: 0 },
            testimonials: { total: 1, byTrainer: { 'Alex Rivera': 1 } },
            trust: { 'Alex Rivera': { ...TRUST_FIXTURE['Alex Rivera'] } },
            // Work left open when the shift ended: the walk Priya paused and the
            // report she never submitted, both on the shared progress clock.
            inProgress: [{
                trainer: 'Priya Patel',
                kind: 'close',
                startedAt: closeWindowIso(businessDate, '20:05'),
                lastActivityAt: closeWindowIso(businessDate, '20:41'),
                expiresAt: closeWindowIso(businessDate, '23:11'),
                expired: false,
                // Task-backed runs keep no step state, so the server sends no counts.
                stepsDone: null,
                stepsTotal: null,
                articleTitle: '',
            }, {
                trainer: 'Priya Patel',
                kind: 'sop:sop-deep-cleaning-a-bathroom',
                startedAt: closeWindowIso(businessDate, '18:12'),
                lastActivityAt: closeWindowIso(businessDate, '18:26'),
                expiresAt: closeWindowIso(businessDate, '20:56'),
                expired: true,
                stepsDone: 1,
                stepsTotal: 4,
                articleTitle: 'SOP: Deep cleaning a bathroom',
            }],
            reportDrafts: [{
                trainer: 'Priya Patel',
                updatedAt: closeWindowIso(businessDate, '20:38'),
                expiresAt: closeWindowIso(businessDate, '23:08'),
                expired: false,
                progress: { filled: 5, total: 8 },
            }],
        },
        findings: seedFindings(businessDate, timeline),
        boothSnapshots: [seedBoothSnapshot(businessDate)],
        digestPostedAt: nextMorning,
        digestText: [
            `Close audit, ${businessDate} (gym ${gymId}), closed by Alex Rivera`,
            'Close tasks: 5 done, 1 not needed. Report 7:42pm with 2 close tasks open.',
            'Headsets: 13 off, 1 on at 8:30pm. Flow not finished.',
            'Photos: 1 verified, 1 for review (iPad 14: cable not visible).',
            'Testimonials: 1.',
            'Flags: 7',
        ].join('\n'),
    };
};

const ensureShiftAudit = (businessDate, gymId) => {
    if (!DAY_RE.test(String(businessDate || ''))) throw new Error('businessDate must be YYYY-MM-DD.');
    const audits = getStore().shiftAudits;
    const key = `${businessDate}#${gymId}`;
    // The nightly job runs after close, so the current business day has no audit yet.
    if (!audits[key]) audits[key] = seedShiftAudit(businessDate, gymId, { audited: businessDate < businessToday() });
    return audits[key];
};

// --- Operating hours -------------------------------------------------------

const DEFAULT_MOCK_HOURS = {
    open: '06:00',
    close: '20:00',
    timezoneOffsetHours: -6,
    openWindowMinutes: 120,
    closeWindowMinutes: 90,
    byWeekday: {
        0: { open: '06:00', close: '20:00' },
        1: { open: '06:00', close: '20:00' },
        2: { open: '06:00', close: '20:00' },
        3: { open: '06:00', close: '20:00' },
        4: { open: '06:00', close: '20:00' },
        5: { open: '09:00', close: '15:00' },
        6: { open: '11:00', close: '15:00' },
    },
    closedDates: [],
    bayDoorTempF: 70,
    // Meridian, Idaho: the weather mock reads here.
    location: { lat: 43.6121, lon: -116.3915 },
};

const normalizeClosedDates = (list) =>
    [...new Set((Array.isArray(list) ? list : []).map((d) => String(d).trim()).filter((d) => DAY_RE.test(d)))].sort();

/** Seeded with two holidays ahead so the closed-dates list has rows offline. */
const getOperatingHours = (gymId) => {
    const all = getStore().operatingHours;
    if (!all[gymId]) {
        all[gymId] = { ...DEFAULT_MOCK_HOURS, gymId, closedDates: [dateOnly(9), dateOnly(45)] };
    }
    return all[gymId];
};

/** `todayClosed` / `nextClosedDate` are derived, the way the server derives them. */
const operatingHoursView = (gymId) => {
    const hours = clone(getOperatingHours(gymId));
    const today = businessToday();
    return {
        ...hours,
        gymId,
        configured: true,
        todayClosed: hours.closedDates.includes(today),
        nextClosedDate: hours.closedDates.find((d) => d > today) || null,
        locationSource: hours.location ? 'gym' : null,
        thresholdSource: hours.bayDoorTempF == null ? 'default' : 'gym',
    };
};

/**
 * Findings nobody has acted on: the day's own, and the trailing week's. The
 * week only counts days the store has already served, which is as much history
 * as an offline demo has.
 */
const unreviewedCounts = (audit, gymId) => {
    const weekStart = shiftDay(audit.businessDate, -6);
    const week = Object.values(getStore().shiftAudits)
        .filter((a) => String(a.gymId) === String(gymId)
            && a.businessDate >= weekStart && a.businessDate <= audit.businessDate)
        .reduce((total, a) => total + a.findings.filter((f) => !f.review).length, 0);
    return { unreviewedCount: audit.findings.filter((f) => !f.review).length, unreviewedWeek: week };
};

/** Idempotent manual run: fills a shell, re-stamps an audited day, posts the digest at most once. */
const runShiftAuditDay = (businessDate, gymId) => {
    const audits = getStore().shiftAudits;
    const existing = ensureShiftAudit(businessDate, gymId);
    const audit = existing.generatedAt ? existing : seedShiftAudit(businessDate, gymId, { audited: true });
    audit.generatedAt = iso(0);
    audit.trigger = 'manual';
    audit.runCount = (existing.runCount || 0) + 1;
    if (!existing.digestPostedAt) audit.digestPostedAt = iso(0);
    audits[`${businessDate}#${gymId}`] = audit;
    return audit;
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const pct = (num, den) => (den > 0 ? Math.min(100, Math.round((num / den) * 100)) : 0);

// Mirror of the backend's announcement scheduling: evaluated on read, no cron.
const announcementStatus = (a, now = new Date()) => {
    const parse = (v) => (v ? new Date(v) : null);
    const start = parse(a.startAt);
    const expires = parse(a.expiresAt);
    const rec = a.recurrence || 'none';
    if (expires && expires.getTime() < now.getTime()) return 'expired';
    if (rec === 'none') {
        return start && start.getTime() > now.getTime() ? 'scheduled' : 'active';
    }
    const startDay = start && new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (startDay && startDay.getTime() > nowDay.getTime()) return 'scheduled';
    if (rec === 'daily') return 'active';
    if (rec === 'weekly') return start && now.getDay() === start.getDay() ? 'active' : 'scheduled';
    if (rec === 'monthly') return start && now.getDate() === start.getDate() ? 'active' : 'scheduled';
    return 'active';
};

// Daily-report-derived performance (last 30 days). Mock values derived from
// the seed data so the dashboard has plausible, varied numbers offline.
const dailyMetrics = (t) => {
    const submitted = t.daily.submitted30 ?? (t.daily.submittedToday ? 12 : 4);
    const scheduled = t.scheduledDays30 ?? (t.daily.submittedToday ? 12 : 10);
    const onSchedule = Math.min(submitted, scheduled);
    return {
        dailyRate30: pct(onSchedule, scheduled),
        reports30: submitted,
        reportsOnSchedule30: onSchedule,
        reportsOffSchedule30: Math.max(0, submitted - onSchedule),
        scheduledDays30: scheduled,
        avgPulse: t.daily.avgPulse ?? 4,
        sessions30: t.daily.sessions30 ?? (t.tasks.completed || 0),
        prospects30: t.daily.prospects30 ?? Math.max(1, (t.issues.bugsReported || 0) + 2),
    };
};

const trainerSummary = (t) => ({
    id: t.id,
    name: t.name,
    role: t.role,
    gym: t.gym,
    active: t.active,
    kbCompletionPct: pct(t.kb.requiredAcked, t.kb.requiredAssigned),
    kbOverdue: t.kb.overdue,
    taskCompletionPct: pct(t.tasks.completed, t.tasks.assigned),
    tasksOverdue: t.tasks.overdue,
    submittedToday: t.daily.submittedToday,
    streakDays: t.daily.streakDays,
    scheduledToday: typeof t.scheduledToday === 'boolean' ? t.scheduledToday : null,
    scheduleActive: typeof t.scheduledToday === 'boolean',
    ...dailyMetrics(t),
    performance: t.performance || null,
    openIssues: t.issues.bugsReported + t.issues.incidentsReported,
});

const buildOverview = (gym) => {
    const { issues, flaggedStale } = getStore();
    const all = getStore().trainers;
    const trainers = gym && gym !== 'All' ? all.filter((t) => t.gym === gym) : all;
    const heroes = trainers.filter((t) => t.role === 'HeroTrainer');

    const kbAssigned = trainers.reduce((s, t) => s + t.kb.requiredAssigned, 0);
    const kbAcked = trainers.reduce((s, t) => s + t.kb.requiredAcked, 0);
    const kbOverdue = trainers.reduce((s, t) => s + t.kb.overdue, 0);

    const tAssigned = trainers.reduce((s, t) => s + t.tasks.assigned, 0);
    const tCompleted = trainers.reduce((s, t) => s + t.tasks.completed, 0);
    const tOverdue = trainers.reduce((s, t) => s + t.tasks.overdue, 0);

    const scheduleActive = trainers.some((t) => typeof t.scheduledToday === 'boolean');
    const expected = scheduleActive
        ? trainers.filter((t) => t.scheduledToday)
        : trainers;
    const submittedToday = expected.filter((t) => t.daily.submittedToday).length;
    const missingToday = expected
        .filter((t) => !t.daily.submittedToday)
        .map((t) => ({ id: t.id, name: t.name }));
    const scheduledToday = scheduleActive
        ? trainers.filter((t) => t.scheduledToday).map((t) => ({ id: t.id, name: t.name }))
        : null;

    const dm = trainers.map(dailyMetrics);
    const pulseVals = dm.map((d) => d.avgPulse).filter((v) => v > 0);
    const teamAvgPulse = pulseVals.length ? Math.round((pulseVals.reduce((s, v) => s + v, 0) / pulseVals.length) * 10) / 10 : 0;
    const teamSessions = dm.reduce((s, d) => s + (d.sessions30 || 0), 0);
    const teamProspects = dm.reduce((s, d) => s + (d.prospects30 || 0), 0);

    return {
        scope: { gym: gym || 'All', gyms: GYMS },
        headcount: { total: trainers.length, heroTrainers: heroes.length },
        kb: {
            completionPct: pct(kbAcked, kbAssigned),
            assigned: kbAssigned,
            acknowledged: kbAcked,
            overdue: kbOverdue,
            flaggedStale: flaggedStale.length,
        },
        tasks: {
            completionPct: pct(tCompleted, tAssigned),
            assigned: tAssigned,
            completed: tCompleted,
            overdue: tOverdue,
        },
        daily: {
            submittedToday,
            total: expected.length,
            submittedPct: pct(submittedToday, expected.length),
            missingToday,
            scheduleActive,
            scheduledToday,
            avgPulse: teamAvgPulse,
            sessions30: teamSessions,
            prospects30: teamProspects,
        },
        issues: {
            openBugs: issues.openBugs,
            openIncidents: issues.openIncidents,
            last7DaysBugs: issues.last7DaysBugs,
            last7DaysIncidents: issues.last7DaysIncidents,
        },
        // Period-over-period deltas (vs the prior 7 days). Positive = improving,
        // except `openIssues` where positive means more issues. Mocked for now;
        // the real backend will compute these from history snapshots.
        trend: {
            kbCompletionPct: 4,
            taskCompletionPct: 6,
            dailyPct: -3,
            openIssues: 2,
        },
        brief: {
            headline: missingToday.length
                ? `${missingToday.length} daily report${missingToday.length === 1 ? '' : 's'} still missing`
                : 'All scheduled reports in',
            bullets: [
                missingToday.length
                    ? `Missing today: ${missingToday.map((m) => m.name).join(', ')}`
                    : 'Everyone scheduled has reported in',
                `${tOverdue} overdue task${tOverdue === 1 ? '' : 's'} across the roster`,
                `${kbOverdue} overdue required-reading item${kbOverdue === 1 ? '' : 's'}`,
            ],
        },
    };
};

// --- Command handlers ------------------------------------------------------

const handlers = {
    get_admin_overview: ([gym] = []) => ({
        overview: buildOverview(gym),
        flaggedStale: getStore().flaggedStale.map((f) => ({ ...f })),
        recentIssues: getStore().issues.recent.map((i) => ({ ...i })),
    }),

    get_trainers: ([gym] = []) => {
        const all = getStore().trainers;
        const scoped = gym && gym !== 'All' ? all.filter((t) => t.gym === gym) : all;
        return scoped.map(trainerSummary);
    },

    get_announcements: ([gym, includeInactive] = []) => {
        const now = new Date();
        const order = { active: 0, scheduled: 1, expired: 2 };
        return getStore()
            .announcements.map((a) => {
                const status = announcementStatus(a, now);
                return { ...a, status, active: status === 'active' };
            })
            .filter((a) => includeInactive || a.status === 'active')
            .filter((a) => !gym || gym === 'All' || a.gym === 'All' || a.gym === gym)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3));
    },

    create_announcement: ([payload]) => {
        const p = payload || {};
        if (!p.title || !p.title.trim()) throw new Error('Announcement needs a title.');
        const rec = ['none', 'daily', 'weekly', 'monthly'].includes(p.recurrence) ? p.recurrence : 'none';
        const announcement = {
            id: nextId('ann'),
            title: p.title.trim(),
            body: (p.body || '').trim(),
            level: ANNOUNCEMENT_LEVELS.includes(p.level) ? p.level : 'info',
            author: p.author || 'Manager',
            gym: p.gym || 'All',
            createdAt: iso(0),
            startAt: p.startAt || '',
            expiresAt: p.expiresAt || '',
            recurrence: rec,
        };
        getStore().announcements = [announcement, ...getStore().announcements];
        return { created: true, announcement };
    },

    delete_announcement: ([id]) => {
        const before = getStore().announcements.length;
        getStore().announcements = getStore().announcements.filter((a) => a.id !== id);
        return { deleted: before !== getStore().announcements.length };
    },

    get_assignable_articles: () => ASSIGNABLE_ARTICLES.map((a) => ({ ...a })),

    get_trainer_detail: ([trainerId]) => {
        const t = getStore().trainers.find((x) => x.id === trainerId || x.name === trainerId);
        if (!t) throw new Error('Trainer not found.');
        return {
            id: t.id,
            name: t.name,
            role: t.role,
            gym: t.gym,
            active: t.active,
            joinedAt: t.joinedAt,
            kb: { ...t.kb, completionPct: pct(t.kb.requiredAcked, t.kb.requiredAssigned) },
            tasks: { ...t.tasks, completionPct: pct(t.tasks.completed, t.tasks.assigned) },
            daily: {
                ...t.daily,
                ...dailyMetrics(t),
                submitted30: dailyMetrics(t).reports30,
                submittedOnSchedule30: dailyMetrics(t).reportsOnSchedule30,
                reportsOffSchedule30: dailyMetrics(t).reportsOffSchedule30,
                scheduledDays30: dailyMetrics(t).scheduledDays30,
                submissionRate: dailyMetrics(t).dailyRate30,
            },
            performance: t.performance || null,
            deputyName: t.deputyName || '',
            issues: { ...t.issues },
            recentTasks: (t.recentTasks || []).map((x) => ({ ...x })),
            kbAssignments: (t.kbAssignments || []).map((x) => ({ ...x })),
        };
    },

    assign_required_reading: ([articleId, trainerIds, dueDate]) => {
        const { trainers } = getStore();
        const article = ASSIGNABLE_ARTICLES.find((a) => a.id === articleId || a.title === articleId);
        const title = article ? article.title : articleId;
        const targets = trainers.filter((t) => (trainerIds || []).includes(t.id));
        targets.forEach((t) => {
            const exists = (t.kbAssignments || []).some((k) => k.articleId === (article ? article.id : articleId));
            if (!exists) {
                t.kbAssignments = [
                    { articleId: article ? article.id : articleId, title, acknowledged: false, dueDate: dueDate || dateOnly(7) },
                    ...(t.kbAssignments || []),
                ];
                t.kb.requiredAssigned += 1;
            }
        });
        return { assigned: true, article: title, count: targets.length };
    },

    set_trainer_role: ([trainerId, role]) => {
        if (!ROLE_OPTIONS.includes(role)) throw new Error(`Invalid role "${role}".`);
        const t = getStore().trainers.find((x) => x.id === trainerId || x.name === trainerId);
        if (!t) throw new Error('Trainer not found.');
        const previous = t.role;
        t.role = role;
        return { updated: true, trainer: t.name, from: previous, to: role };
    },

    set_trainer_performance_flag: ([trainerId, rating, note = '']) => {
        const t = getStore().trainers.find((x) => x.id === trainerId || x.name === trainerId);
        if (!t) throw new Error('Trainer not found.');
        const normalized = String(rating || '').toLowerCase();
        if (normalized === 'clear') {
            t.performance = null;
            return { updated: true, trainer: t.name, performance: null };
        }
        if (!['praise', 'concern'].includes(normalized)) {
            throw new Error('rating must be praise, concern, or clear.');
        }
        t.performance = {
            rating: normalized,
            note: String(note || '').trim(),
            setBy: 'Mock Manager',
            setAt: iso(0),
        };
        return { updated: true, trainer: t.name, performance: t.performance };
    },

    set_trainer_deputy_name: ([trainerId, deputyName = '']) => {
        const t = getStore().trainers.find((x) => x.id === trainerId || x.name === trainerId);
        if (!t) throw new Error('Trainer not found.');
        t.deputyName = String(deputyName || '').trim();
        return { updated: true, trainer: t.name, deputyName: t.deputyName || null };
    },

    get_deputy_status: () => ({
        configured: false,
        lastSyncedAt: '',
        lastSyncedBy: '',
        trainersWithDays: 0,
        overrideTrainerCount: 0,
    }),

    sync_deputy_schedules: () => {
        throw new Error('Deputy is not configured in mock mode.');
    },

    get_trainer_schedule: ([trainerId, days = 30]) => {
        const store = getStore();
        store.schedules = store.schedules || {};
        const overrides = store.schedules[trainerId] || {};
        const end = new Date();
        const out = [];
        const windowDays = Number(days) || 30;
        for (let i = windowDays - 1; i >= 0; i -= 1) {
            const d = new Date(end);
            d.setDate(d.getDate() - i);
            const key = dateOnly(-i);
            const ov = overrides[key];
            const deputy = i % 3 === 0;
            const action = ov?.action || null;
            const effective = action === 'exclude' ? false : (action === 'include' || deputy);
            out.push({
                date: key,
                deputy,
                override: action,
                overrideNote: ov?.note || '',
                effective,
                source: action === 'include' ? 'override_include'
                    : action === 'exclude' ? 'override_exclude'
                        : deputy ? 'deputy' : 'none',
            });
        }
        return {
            trainer: trainerId,
            days: out,
            effectiveCount: out.filter((d) => d.effective).length,
            windowDays,
            deputyConfigured: false,
            lastSyncedAt: '',
        };
    },

    set_schedule_day_override: ([trainerId, date, action]) => {
        const store = getStore();
        store.schedules = store.schedules || {};
        store.schedules[trainerId] = store.schedules[trainerId] || {};
        const normalized = String(action || '').toLowerCase();
        if (normalized === 'clear') {
            delete store.schedules[trainerId][date];
        } else {
            store.schedules[trainerId][date] = { action: normalized, note: '' };
        }
        return { updated: true, trainer: trainerId, date, action: normalized === 'clear' ? null : normalized };
    },

    get_task_completion_history: ([filters = {}]) => {
        const events = [
            {
                id: 'tc-1',
                completedBy: 'Alex Rivera',
                businessDate: dateOnly(-1),
                completedAt: iso(-1),
                taskId: 'tk-a1',
                taskName: 'Open checklist',
                assignedTo: 'Alex Rivera',
                recurrence: 'daily',
                shiftPhase: 'open',
                requirementLevel: 'required',
                outcome: 'done',
                outcomeLabel: 'Done',
                action: 'completed',
                detail: '',
                periodKey: dateOnly(-1),
            },
            {
                id: 'tc-2',
                completedBy: 'Alex Rivera',
                businessDate: dateOnly(-1),
                completedAt: iso(-1),
                taskId: 'tk-a2',
                taskName: 'Reset booth 3',
                assignedTo: 'On Shift Staff',
                recurrence: 'daily',
                shiftPhase: 'close',
                requirementLevel: 'required',
                outcome: 'done',
                outcomeLabel: 'Done',
                action: 'completed',
                detail: '',
                periodKey: dateOnly(-1),
            },
            {
                id: 'tc-3',
                completedBy: 'Priya Patel',
                businessDate: dateOnly(-2),
                completedAt: iso(-2),
                taskId: 'tk-p1',
                taskName: 'Member follow-ups',
                assignedTo: 'Priya Patel',
                recurrence: 'weekly',
                shiftPhase: 'all_day',
                requirementLevel: 'optional',
                outcome: 'not_needed',
                outcomeLabel: 'Not needed',
                action: 'completed',
                detail: '',
                periodKey: `W:${dateOnly(-7)}`,
            },
            {
                id: 'tc-4',
                completedBy: 'Sam Okafor',
                businessDate: dateOnly(-3),
                completedAt: iso(-3),
                taskId: 'tk-s1',
                taskName: 'Deep clean booths',
                assignedTo: 'On Shift Staff',
                recurrence: 'thrice_weekly',
                shiftPhase: 'close',
                requirementLevel: 'required',
                outcome: 'done',
                outcomeLabel: 'Done',
                action: 'completed',
                detail: '2/3',
                periodKey: `W:${dateOnly(-7)}`,
            },
        ].filter((event) => {
            if (filters.employeeName && event.completedBy !== filters.employeeName) return false;
            if (filters.startDate && event.businessDate < filters.startDate) return false;
            if (filters.endDate && event.businessDate > filters.endDate) return false;
            return event.action === 'completed';
        }).sort((a, b) => `${b.businessDate}${b.completedAt}`.localeCompare(`${a.businessDate}${a.completedAt}`));

        const byDay = {};
        events.forEach((event) => {
            byDay[event.businessDate] = byDay[event.businessDate] || [];
            byDay[event.businessDate].push(event);
        });
        const byPerson = {};
        events.forEach((event) => {
            byPerson[event.completedBy] = byPerson[event.completedBy] || [];
            byPerson[event.completedBy].push(event);
        });

        return {
            events,
            byDay: Object.keys(byDay).sort().reverse().map((businessDate) => ({
                businessDate,
                events: byDay[businessDate],
                count: byDay[businessDate].length,
            })),
            byPerson: Object.keys(byPerson).sort().map((employeeName) => ({
                employeeName,
                events: byPerson[employeeName].sort((a, b) => `${b.businessDate}${b.completedAt}`.localeCompare(`${a.businessDate}${a.completedAt}`)),
                count: byPerson[employeeName].length,
            })),
            totalCompleted: events.length,
            filters,
        };
    },

    reset_task_completion: ([taskId, employeeName]) => ({
        reset: true,
        taskId,
        employeeName,
    }),

    complete_task_for_trainer: ([taskId, employeeName, outcome = 'done']) => ({
        completed: true,
        taskId,
        employeeName,
        outcome,
    }),

    update_task: ([taskId, updates = {}]) => ({
        updated: true,
        taskId,
        ...updates,
    }),

    create_shift_handoff: ([body, gymId] = []) => {
        const text = String(typeof body === 'object' && body ? (body.body || '') : (body || '')).trim();
        if (!text) throw new Error('Handoff note needs a body.');
        const resolvedGym = gymId || (typeof body === 'object' && body ? body.gymId : null);
        const note = {
            id: nextId('ho'),
            body: text,
            gymId: resolvedGym ? String(resolvedGym) : null,
            author: 'Trainer',
            createdAt: iso(0),
            active: true,
        };
        getStore().handoffs = [note, ...(getStore().handoffs || [])];
        return note;
    },

    get_active_shift_handoffs: ([gymId] = []) => {
        const list = getStore().handoffs || [];
        return list
            .filter((n) => n.active !== false)
            .filter((n) => !gymId || !n.gymId || String(n.gymId) === String(gymId))
            .map((n) => ({ ...n }));
    },

    dismiss_shift_handoff: ([noteId]) => {
        const list = getStore().handoffs || [];
        const note = list.find((n) => n.id === noteId);
        if (note) note.active = false;
        return { dismissed: Boolean(note), id: noteId };
    },

    get_fairness_scorecard: ([daysBack = 7] = []) => {
        const trainers = getStore().trainers || [];
        const rows = trainers.map((t) => {
            const done = t.tasks?.completed || 0;
            const notNeeded = Math.max(0, (t.tasks?.assigned || 0) - (t.tasks?.completed || 0) - (t.tasks?.overdue || 0));
            const missed = t.tasks?.overdue || 0;
            return {
                trainerId: t.id,
                name: t.name,
                gym: t.gym,
                done,
                notNeeded,
                missed,
                blocked: 0,
                expectedDays: Number(daysBack) || 7,
                tasksCompleted: done,
                tasksAssigned: done + notNeeded + missed,
            };
        });
        return {
            daysBack: Number(daysBack) || 7,
            windowDays: Number(daysBack) || 7,
            byTrainer: rows,
            rows,
        };
    },

    add_supply_signal: ([text, source = ''] = []) => {
        const body = String(text || '').trim();
        if (!body) throw new Error('Supply signal needs text.');
        const signal = {
            id: nextId('sup'),
            text: body,
            source: source || 'admin',
            status: 'open',
            createdAt: iso(0),
        };
        getStore().supplySignals = [signal, ...(getStore().supplySignals || [])];
        return signal;
    },

    list_supply_signals: () => (getStore().supplySignals || []).map((s) => ({ ...s })),

    resolve_supply_signal: ([id, status]) => {
        const list = getStore().supplySignals || [];
        const signal = list.find((s) => s.id === id);
        if (!signal) throw new Error('Supply signal not found.');
        signal.status = status || 'cleared';
        signal.resolvedAt = iso(0);
        return { ...signal };
    },

    get_operating_hours: ([gymId] = []) => operatingHoursView(String(gymId || '2')),

    set_operating_hours: ([gymId, hours] = []) => {
        const gym = String(gymId || '2');
        if (!hours || typeof hours !== 'object') throw new Error('hours is required.');
        const closedDates = hours.closedDates === undefined
            ? getOperatingHours(gym).closedDates
            : normalizeClosedDates(hours.closedDates);
        getStore().operatingHours[gym] = { ...getOperatingHours(gym), ...hours, closedDates };
        return operatingHoursView(gym);
    },

    get_shift_audit: ([date, gymId] = []) => {
        const gym = String(gymId || '2');
        const audit = clone(ensureShiftAudit(date, gym));
        if (audit.summary) Object.assign(audit.summary, unreviewedCounts(audit, gym));
        return audit;
    },

    run_shift_audit: ([date, endDate, gymId] = []) => {
        const gym = String(gymId || '2');
        if (!DAY_RE.test(String(date || ''))) throw new Error('Dates must be YYYY-MM-DD.');
        if (!endDate) return clone(runShiftAuditDay(date, gym));
        if (!DAY_RE.test(String(endDate))) throw new Error('Dates must be YYYY-MM-DD.');
        if (endDate < date) throw new Error('endDate must be on or after date.');
        const days = [];
        for (let day = date; day <= endDate; day = shiftDay(day, 1)) {
            if (days.length >= AUDIT_RANGE_CAP_DAYS) throw new Error(`Audit range is capped at ${AUDIT_RANGE_CAP_DAYS} days.`);
            const audit = runShiftAuditDay(day, gym);
            days.push({
                businessDate: day,
                gymId: gym,
                findings: audit.findings.length,
                digestPosted: Boolean(audit.digestPostedAt),
            });
        }
        return { days };
    },

    review_task_completion: ([eventId, outcome, note = ''] = []) => {
        if (!eventId) throw new Error('eventId is required.');
        const stored = REVIEW_OUTCOMES[outcome];
        if (!stored) throw new Error('outcome must be confirm, reject or discussed.');
        const trimmed = String(note || '').trim();
        if (OUTCOMES_NEEDING_NOTE.includes(outcome) && !trimmed) {
            throw new Error(`A note is required to ${outcome === 'reject' ? 'reject' : 'log a discussion'}.`);
        }
        if (trimmed.length > REVIEW_NOTE_MAX) throw new Error(`A note is limited to ${REVIEW_NOTE_MAX} characters.`);
        const review = { outcome: stored, by: 'Mock Manager', at: iso(0), note: trimmed };
        let matched = false;
        Object.values(getStore().shiftAudits).forEach((audit) => {
            audit.findings.filter((f) => f.eventId === eventId).forEach((f) => { f.review = { ...review }; matched = true; });
            audit.timeline.filter((e) => e.id === eventId).forEach((e) => { e.review = { ...review }; matched = true; });
        });
        if (!matched) throw new Error('Completion event not found.');
        // Reject reopens the task for the trainer; confirm changes nothing on the task.
        return { eventId, review, reset: outcome === 'reject' ? true : null };
    },

    get_task_evidence_view_url: ([photoKey] = []) => {
        if (!photoKey || typeof photoKey !== 'string') throw new Error('photoKey is required.');
        return { url: evidencePlaceholder(photoKey), expiresIn: 600 };
    },

    get_trainer_trust: ([trainerName, days = 30] = []) => {
        if (!trainerName) throw new Error('trainerName is required.');
        return trustFor(String(trainerName), days);
    },

    get_feature_flags: () => featureFlagsView(),

    set_feature_flag: ([name, value] = []) => {
        // Presence, not truthiness: every one of these defaults to a boolean.
        if (!Object.prototype.hasOwnProperty.call(FEATURE_FLAG_DEFAULTS, String(name))) {
            throw new Error(`Unknown feature flag "${name}".`);
        }
        if (typeof value !== 'boolean') throw new Error('value must be true or false.');
        getStore().featureFlags[String(name)] = value;
        getStore().featureFlagsMeta = { updatedAt: iso(0), updatedBy: 'Mock Admin' };
        return featureFlagsView();
    },
};

/** Drop-in replacement for executeCommand for admin commands. */
export const mockExecuteAdminCommand = (command, args = []) =>
    new Promise((resolve, reject) => {
        setTimeout(() => {
            const handler = handlers[command];
            if (!handler) {
                resolve(JSON.stringify({ success: false, error: `Mock: unknown admin command "${command}".` }));
                return;
            }
            try {
                resolve({ success: true, data: handler(args || []) });
            } catch (e) {
                reject(e);
            }
        }, NETWORK_DELAY_MS);
    });

// --- Manager analytics -----------------------------------------------------
//
// Appended so the reliability table and the task heatmap are exercisable
// offline. Both windows end on the business day (`businessToday()`), so the
// fixtures move with the clock the way the real aggregates do; only the counts
// are frozen.

const RELIABILITY_SEED = [
    {
        trainer: 'Alex Rivera',
        shiftsWorked: 18, reportsSubmitted: 17, reportStreak: 9,
        done: 214, notNeeded: 12, blocked: 3,
        flagsByCode: { burst: 4, photo_stale: 2, headset_on: 1 },
        mediumPlus: 3,
        reviews: { confirmed: 5, rejected: 1, discussed: 2 },
        headsetsOffRate: 0.94, testimonials: 21, cleanCloses: 15,
    },
    {
        trainer: 'Priya Patel',
        shiftsWorked: 15, reportsSubmitted: 11, reportStreak: 2,
        done: 168, notNeeded: 21, blocked: 6,
        flagsByCode: { burst: 9, missing_report: 4, photo_uncertain: 3, replayed: 2 },
        mediumPlus: 13,
        reviews: { confirmed: 2, rejected: 4, discussed: 1 },
        headsetsOffRate: 0.71, testimonials: 6, cleanCloses: 5,
    },
    {
        trainer: 'Sam Okafor',
        shiftsWorked: 9, reportsSubmitted: 9, reportStreak: 9,
        done: 103, notNeeded: 4, blocked: 0,
        flagsByCode: {},
        mediumPlus: 0,
        reviews: { confirmed: 0, rejected: 0, discussed: 0 },
        // No booth telemetry for this trainer's gym nights yet.
        headsetsOffRate: null, testimonials: 14, cleanCloses: 9,
    },
    {
        trainer: 'Maria Gomez',
        shiftsWorked: 12, reportsSubmitted: 10, reportStreak: 0,
        done: 141, notNeeded: 9, blocked: 2,
        flagsByCode: { photo_stale: 5, burst: 1 },
        mediumPlus: 6,
        reviews: { confirmed: 3, rejected: 0, discussed: 4 },
        headsetsOffRate: 0.88, testimonials: 11, cleanCloses: 8,
    },
];

// Counts scale with the window so a 7-day view isn't identical to a 56-day one.
const scaleReliability = (row, days) => {
    const factor = days / 28;
    const s = (n) => Math.round(n * factor);
    return {
        ...row,
        shiftsWorked: s(row.shiftsWorked),
        reportsSubmitted: s(row.reportsSubmitted),
        reportStreak: Math.min(row.reportStreak, s(row.shiftsWorked)),
        done: s(row.done),
        notNeeded: s(row.notNeeded),
        blocked: s(row.blocked),
        flagsByCode: Object.fromEntries(
            Object.entries(row.flagsByCode).map(([code, n]) => [code, Math.max(1, s(n))])
        ),
        mediumPlus: s(row.mediumPlus),
        reviews: {
            confirmed: s(row.reviews.confirmed),
            rejected: s(row.reviews.rejected),
            discussed: s(row.reviews.discussed),
        },
        nudgesAnswered: null,
        testimonials: s(row.testimonials),
        cleanCloses: s(row.cleanCloses),
    };
};

const HEATMAP_TASKS = [
    { taskId: 'tk-15', taskName: 'Open: boot booths', recurrence: 'daily', shiftPhase: 'open' },
    { taskId: 'tk-16', taskName: 'Open: bathrooms check', recurrence: 'daily', shiftPhase: 'open' },
    { taskId: 'seed-member-testimonials', taskName: 'Get 2+ member testimonials', recurrence: 'daily', shiftPhase: 'all_day' },
    { taskId: 'tk-18', taskName: 'Deep clean a bathroom', recurrence: 'weekly', shiftPhase: 'all_day' },
    { taskId: 'tk-10', taskName: 'Close: shutdown booths', recurrence: 'daily', shiftPhase: 'close' },
    { taskId: 'tk-11', taskName: 'Close: plug in iPad 14', recurrence: 'daily', shiftPhase: 'close' },
    { taskId: 'tk-12', taskName: 'Close: mop as needed', recurrence: 'daily', shiftPhase: 'close' },
];

const HEATMAP_NAMES = ['Alex Rivera', 'Priya Patel', 'Sam Okafor', 'Maria Gomez'];

/**
 * Deterministic per task/day so a refetch of the same window repaints
 * identically. `index` counts back from the newest day, which is what pins the
 * two seeded misses and the duplicate to fixed offsets.
 */
const heatmapCell = (task, index) => {
    // A weekly task only applies on its one weekday; other days are blank.
    if (task.recurrence === 'weekly' && index % 7 !== 3) return { outcome: '', by: [] };
    const by = [HEATMAP_NAMES[(index + task.taskId.length) % HEATMAP_NAMES.length]];
    if (task.taskId === 'tk-12' && index % 5 === 2) return { outcome: 'not_needed', by };
    if (task.taskId === 'tk-16' && index === 6) return { outcome: 'blocked', by };
    return { outcome: 'done', by };
};

const MISSES = [{ taskId: 'tk-11', offset: 2 }, { taskId: 'tk-10', offset: 5 }];
const DUPLICATE = { taskId: 'seed-member-testimonials', offset: 1, names: ['Alex Rivera', 'Priya Patel'] };

handlers.get_trainer_reliability = ([gymId, days = 28] = []) => {
    const window = Number(days) || 28;
    const end = businessToday();
    return {
        gymId: String(gymId || '2'),
        days: window,
        start: shiftDay(end, -(window - 1)),
        end,
        trainers: RELIABILITY_SEED.map((row) => scaleReliability(row, window)),
    };
};

handlers.get_task_heatmap = ([gymId, days = 14] = []) => {
    const window = Number(days) || 14;
    const end = businessToday();
    const dayList = Array.from({ length: window }, (_, i) => shiftDay(end, -(window - 1 - i)));
    const dayIndex = (offset) => window - 1 - offset;

    const tasks = HEATMAP_TASKS.map((task) => {
        const cells = {};
        dayList.forEach((date, i) => {
            const index = window - 1 - i;
            const miss = MISSES.find((m) => m.taskId === task.taskId && m.offset === index);
            if (miss) {
                cells[date] = { outcome: 'missed', by: [] };
                return;
            }
            if (task.taskId === DUPLICATE.taskId && index === DUPLICATE.offset) {
                cells[date] = { outcome: 'done', by: [...DUPLICATE.names] };
                return;
            }
            cells[date] = heatmapCell(task, index);
        });
        return { ...task, cells };
    });

    const duplicateDate = dayList[dayIndex(DUPLICATE.offset)];
    return {
        gymId: String(gymId || '2'),
        days: dayList,
        tasks,
        duplicates: duplicateDate
            ? [{ taskId: DUPLICATE.taskId, date: duplicateDate, names: [...DUPLICATE.names] }]
            : [],
    };
};
